// axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动
import axios, { AxiosResponse } from 'axios';
import { MessagePlugin } from 'tdesign-vue-next';

import router from '@/router';
import { getToken, removeToken } from '@/utils/auth';

const pendingRequests: Array<{
  config: any;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

let isRefreshing = false; // 是否正在登录中，防止重复跳转

export const request = axios.create({
  baseURL: '/api/v1',
});

request.interceptors.request.use((config) => {
  const token = getToken(); // 从本地存储获取 token
  if (token) {
    config.headers.Authorization = token;
  }
  return config;
});

request.interceptors.response.use(
  async (rsp: AxiosResponse) => {
    if (rsp.status !== 200) {
      MessagePlugin.error(rsp.data.msg || '未知错误');
      return Promise.reject(new Error(rsp.data.msg || '未知错误'));
    }
    // 判断是否为文件流响应
    const contentType = rsp.headers['content-type'];

    if (
      contentType?.includes('application/octet-stream') ||
      contentType?.includes('application/vnd.ms-excel') ||
      contentType?.includes('application/vnd.openxmlformats-officedocument') ||
      contentType?.includes('application/pdf')
    ) {
      // 将arraybuffer转换为blob
      const blob = new Blob([rsp.data], { type: contentType });
      return blob;
    }

    const { code } = rsp.data;
    if (rsp.data instanceof ArrayBuffer) {
      try {
        const decoder = new TextDecoder('utf-8');
        const text = decoder.decode(rsp.data);
        const jsonData = JSON.parse(text);
        rsp.data = jsonData;
      } catch (error) {
        console.error('无法将ArrayBuffer转换为JSON:', error);
        // 如果转换失败，保持原样
      }
    }
    // 认证失败
    if (code === 401) {
      MessagePlugin.error(rsp.data.msg || '未知错误');

      // 如果是登录请求,直接抛出错误
      if (rsp.config.url?.includes('/login')) {
        return Promise.reject(new Error(rsp.data.msg || '未知错误'));
      }

      // 存储当前请求
      const retryRequest = new Promise((resolve, reject) => {
        pendingRequests.push({
          config: rsp.config,
          resolve,
          reject,
        });
      });

      if (!isRefreshing) {
        isRefreshing = true;
        removeToken();
        const redirect = router.currentRoute.value.path;
        if (redirect !== '/login') {
          router.push({ path: '/login', query: { redirect } });
        } else {
          router.push('/login');
        }
      }

      return retryRequest; // 返回一个等待重试的 Promise
    }

    // 没权限
    if (code === 403) {
      console.warn('请求403');
      MessagePlugin.error('没有访问权限');
      router.push('/result/403');
      return Promise.reject(new Error('没有访问权限'));
    }

    // 代理商未配置价格
    if (code === 40405) {
      MessagePlugin.error(rsp.data.msg || '未知错误');
      return Promise.reject(rsp.data);
    }

    // 数据导入失败
    if (code === 620) {
      return Promise.reject(rsp.data);
    }

    // 非APP下单
    if (code === 400117) {
      return Promise.reject(rsp.data);
    }

    // 订单状态错误 && 订单未找到
    if (code === 400278 || code === 40411) {
      MessagePlugin.error(rsp.data.msg || '未知错误');
      return Promise.reject(rsp.data.msg);
    }

    if (code !== 200) {
      MessagePlugin.error(rsp.data.msg || '未知错误');

      return Promise.reject(new Error(rsp.data.msg || '未知错误'));
    }
    return Promise.resolve(rsp) as any;
  },
  (error) => {
    MessagePlugin.error('请求失败，请检查网络连接');
    return Promise.reject(error);
  },
);

export const retryPendingRequests = async () => {
  isRefreshing = false;

  // 重新发送所有等待中的请求
  const requests = [...pendingRequests];
  pendingRequests.length = 0;

  const promises = requests.map(async ({ config, resolve, reject }) => {
    try {
      const response = await request(config);
      resolve(response);
    } catch (err) {
      reject(err);
    }
  });

  await Promise.allSettled(promises);
};
